import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Ruler,
  Package,
  Lock,
  Thermometer,
  CheckCircle,
  ArrowRight,
  Calendar,
} from "lucide-react";

export default function ContainersPage() {

  const containers = [
    {
      id: 1,
      title: "20ft Standard Container",
      size: "20ft",
      image:
        "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=800",
      dimensions: {
        length: "6.06m (19'10\")",
        width: "2.44m (8'0\")",
        height: "2.59m (8'6\")",
        capacity: "33.2 m³ (1,172 ft³)",
      },
      features: [
        "Weather-resistant steel construction",
        "Ground-level access",
        "Secure locking system",
        "Perfect for household storage",
        "Ideal for trade equipment",
      ],
      color: "bg-blue-50",
    },
    {
      id: 2,
      title: "40ft High Cube Container",
      size: "40ft",
      image:
        "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800",
      dimensions: {
        length: "12.19m (40'0\")",
        width: "2.44m (8'0\")",
        height: "2.90m (9'6\")",
        capacity: "76.3 m³ (2,694 ft³)",
      },
      features: [
        "Extra height for tall items",
        "Double the storage capacity",
        "Perfect for pallets & machinery",
        "Commercial-grade durability",
        "Cost-effective bulk storage",
      ],
      color: "bg-emerald-50",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
              Our Containers
            </h1>
            <p className="text-xl md:text-2xl text-zinc-300 leading-relaxed">
              High-quality steel containers built to protect your belongings. 
              Choose from two sizes designed for different storage requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Containers Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            {containers.map((container, index) => (
              <motion.div
                key={container.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group bg-white rounded-2xl border border-zinc-200 overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1"
              >
                {/* Image */}
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={container.image}
                    alt={container.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                    <span className="font-bold text-zinc-900">{container.size}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h2 className="text-3xl font-bold mb-2">
                    {container.title}
                  </h2>
                  <p className="text-zinc-500 mb-6">
                    {container.size === "20ft" 
                      ? "Perfect for personal & trade use"
                      : "Ideal for commercial storage"}
                  </p>

                  <div className={`${container.color} p-6 rounded-xl mb-6 border border-zinc-200/50`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${
                        container.size === "20ft" 
                          ? "bg-blue-100" 
                          : "bg-emerald-100"
                      }`}>
                        <Ruler size={20} className={
                          container.size === "20ft" 
                            ? "text-blue-600" 
                            : "text-emerald-600"
                        } />
                      </div>
                      <h3 className="font-bold text-lg">Dimensions</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm text-zinc-700">
                      <div>
                        <span className="font-semibold text-zinc-900">Length:</span>
                        <p>{container.dimensions.length}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-zinc-900">Width:</span>
                        <p>{container.dimensions.width}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-zinc-900">Height:</span>
                        <p>{container.dimensions.height}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-zinc-900">Capacity:</span>
                        <p>{container.dimensions.capacity}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    {container.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-3">
                        <CheckCircle
                          size={20}
                          className={
                            container.size === "20ft"
                              ? "text-blue-600 mt-0.5 flex-shrink-0"
                              : "text-emerald-600 mt-0.5 flex-shrink-0"
                          }
                        />
                        <span className="text-zinc-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/booking"
                    className="inline-flex items-center gap-2 w-full justify-center bg-zinc-900 text-white px-6 py-3 rounded-xl font-semibold hover:bg-zinc-800 transition shadow-lg hover:shadow-xl group-hover:scale-105"
                  >
                    Book This Container <ArrowRight size={18} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Features Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h3 className="text-3xl font-bold text-center mb-12">
              Why Our Containers Stand Out
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  icon: <Lock size={28} />,
                  title: "Secure",
                  desc: "Heavy-duty locking mechanisms",
                  gradient: "from-blue-500 to-blue-600",
                  bg: "bg-blue-50",
                },
                {
                  icon: <Thermometer size={28} />,
                  title: "Weatherproof",
                  desc: "Protected from elements",
                  gradient: "from-emerald-500 to-emerald-600",
                  bg: "bg-emerald-50",
                },
                {
                  icon: <Package size={28} />,
                  title: "Spacious",
                  desc: "Maximize your storage",
                  gradient: "from-amber-500 to-amber-600",
                  bg: "bg-amber-50",
                },
                {
                  icon: <Ruler size={28} />,
                  title: "Standard",
                  desc: "ISO-certified containers",
                  gradient: "from-rose-500 to-rose-600",
                  bg: "bg-rose-50",
                },
              ].map((item, idx) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  className={`${item.bg} p-6 rounded-xl border border-zinc-200 hover:shadow-lg transition-all hover:-translate-y-1`}
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center shadow-lg mb-4`}>
                    <div className="text-white">{item.icon}</div>
                  </div>
                  <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                  <p className="text-sm text-zinc-700">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-3xl p-12 text-white text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Book Your Container?
              </h3>
              <p className="text-zinc-300 text-lg mb-8 max-w-2xl mx-auto">
                Start your booking process now and secure your storage space today.
              </p>
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 bg-white text-zinc-900 px-8 py-4 rounded-xl font-semibold hover:bg-zinc-100 transition shadow-lg hover:shadow-xl"
              >
                <Calendar size={20} />
                Book Now <ArrowRight size={20} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
}
